import React, { useCallback, useMemo } from 'react'
import * as Yup from 'yup'
import { Button, FormGroup, I18nHtml, JSONFetcher, postValues } from 'shared'
import { CtaEditor, NewsletterRecipientsSelection } from 'orga_manage'
import { Field, Form, Formik } from 'formik'
import {
  Newsletter,
  NewsletterFormProps,
  NewsletterFormSubmitHandler,
  NewsletterFormValues,
  NewsletterSubscribeResponse,
} from './types'
import { TrixEditor } from 'modules/trix_editor'

export const NewsletterForm = ({
  formUrl,
  backUrl,
  newsletter: newsletter_,
  apiBaseUrl,
  imageUploadUrl,
  projectSubscriptionOptions,
}: NewsletterFormProps): JSX.Element => {
  const newsletter = useMemo(() => JSON.parse(newsletter_) as Newsletter, [newsletter_])
  const newRecord = useMemo(() => !newsletter.id, [newsletter])
  const initialValues: NewsletterFormValues = {
    project_id: newsletter.project_id || '',
    lang: newsletter.lang || '',
    subject: newsletter.subject || '',
    body: newsletter.body || '',
    // Toggles the Yup validation and represents the CtaEditor checkbox that toggles the cta form.
    _has_call_to_action: newRecord || newsletter.call_to_action_headline !== '',
    call_to_action_headline: newsletter.call_to_action_headline || '',
    call_to_action_button: newsletter.call_to_action_button || '',
    call_to_action_url: newsletter.call_to_action_url || '',
    _interval: '',
    _amount: '',
  }

  const submitHandler: NewsletterFormSubmitHandler = useCallback(
    (values, formikBag) => {
      const newsletterValues = postValues(values)

      JSONFetcher[newRecord ? 'post' : 'put']({
        url: formUrl,
        body: {
          organisation_newsletter: {
            ...newsletterValues,
            lang: (newsletterValues.lang === '' && null) || newsletterValues.lang,
          },
        },
        success: (response: NewsletterSubscribeResponse) => {
          if (response.redirect_url) {
            window.location.href = response.redirect_url
          } else {
            Object.entries(response.errors).forEach(([field, message]) => {
              formikBag.setFieldError(field, message)
            })
            formikBag.setSubmitting(false)
          }
        },
      })
    },
    [newRecord, formUrl]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        subject: Yup.string()
          .required(i18n.t('manage.newsletter_form.errors.blank'))
          .max(255, i18n.t('manage.newsletter_form.errors.too_long')),
        body: Yup.string().required(i18n.t('manage.newsletter_form.errors.blank')),
        call_to_action_headline: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.newsletter_form.errors.blank'))
              .max(255, i18n.t('manage.newsletter_form.errors.too_long')),
        }),
        call_to_action_button: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.newsletter_form.errors.blank'))
              .max(255, i18n.t('manage.newsletter_form.errors.too_long')),
        }),
        call_to_action_url: Yup.string().when('_has_call_to_action', {
          is: true,
          then: () =>
            Yup.string()
              .required(i18n.t('manage.newsletter_form.errors.blank'))
              .max(255, i18n.t('manage.newsletter_form.errors.too_long')),
        }),
      }),
    []
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={submitHandler}
    >
      {(formikProps) => (
        <Form noValidate data-testid="newsletter-form">
          <h3>{i18n.t('manage.newsletter_form.headline_subscription_filter_html')}</h3>

          <NewsletterRecipientsSelection
            projectSubscriptionOptions={projectSubscriptionOptions}
            apiBaseUrl={apiBaseUrl}
          />

          <h3>{i18n.t('manage.newsletter_form.headline_content_html')}</h3>

          <Field
            name="subject"
            maxLength={255}
            label={i18n.t('manage.newsletter_form.subject_label')}
            component={FormGroup}
            required
          />

          <Field
            type="hidden"
            name="body"
            label={i18n.t('manage.newsletter_form.body_label')}
            component={FormGroup}
            required
          >
            <TrixEditor
              imageUploadUrl={imageUploadUrl}
              showVideoButton={true}
              onChange={(value) => void formikProps.setFieldValue('body', value)}
              value={formikProps.values.body}
            />
          </Field>

          <CtaEditor model="newsletter" />

          <div className="generic-box-actions">
            <Button type="submit" className="btn btn-primary btn-large" loading={formikProps.isSubmitting}>
              {i18n.t('verbs.preview')}
            </Button>
            <I18nHtml i18nKey="manage.newsletter_form.publish_next_step_html" /> <span>{i18n.t('misc.or')}</span>{' '}
            <a href={backUrl} className="hover:text-red-700">
              {i18n.t('verbs.cancel')}
            </a>
          </div>
        </Form>
      )}
    </Formik>
  )
}
